import { BANNER_SITES } from './bannerLayout';
import type { KitBannerCard, KitBannerSite, KitLinkPreviewCard, KitThumbnailCard } from './kitCard';
import { isThumbnailVideo, type ThumbnailVideo } from '../studioThemes';
import { capitalizeKitWord, kitDateParts, kitWords, type KitTextLocale } from '../kitText';
import { isReleasedOn, releaseDateLabel, releaseDayLabel } from './storyCopy';

/**
 * The copy of the kit cards (owner answers 2026-10-08, designs `asset-redesign/g`, `i`, `m`). Code
 * writes every text; the director never does. The words come from the kit text table
 * (`../kitText.ts`) in the language of the kit; `CARD_COPY` is the English set.
 */
export const CARD_COPY = {
  outNow: 'Out now',
  newSingle: 'New single',
  listenOn: 'Listen on',
  out: 'Out',
  now: 'Now',
  coming: 'Coming',
  soon: 'Soon',
  preSaveNow: 'Pre-save now',
  listenNow: 'Listen now',
  comingSoon: 'Coming soon',
} as const;

/**
 * The status of a banner (owner answer 1 of page g): "Out Fri 16 Oct" before the release day, "Out
 * now" from the release day on. A song with no release date says "New single". Pure.
 */
export function bannerStatusText(releaseDate: string | null, today: string, locale?: KitTextLocale | null): string {
  const words = kitWords(locale);
  if (releaseDate === null) {
    return words.newSingle;
  }
  if (isReleasedOn(releaseDate, today)) {
    return words.outNow;
  }
  const day = releaseDayLabel(releaseDate, locale);
  return day === null ? words.newSingle : words.outDate(day);
}

/**
 * The texts of the banner of one site: the one builder of the render (music24-api `buildKitCard`)
 * and of the Studio preview. `link` is the SmartLink address with no scheme (`storyLinkText`);
 * Facebook shows no URL (owner answer 4 of page g). Pure.
 */
export function buildBannerCard(input: {
  site: KitBannerSite;
  title: string;
  artist: string;
  releaseDate: string | null;
  today: string;
  link: string | null;
  /** The language of the words; absent: English. */
  locale?: KitTextLocale | null;
}): KitBannerCard {
  return {
    kind: 'banner',
    site: input.site,
    title: input.title,
    artist: input.artist,
    status: bannerStatusText(input.releaseDate, input.today, input.locale),
    linkLead: kitWords(input.locale).listenOn,
    link: BANNER_SITES[input.site].url ? input.link : null,
  };
}

/** The label of each thumbnail: the type of its video. Code picks it, never the director. */
export const THUMBNAIL_LABELS: Readonly<Record<ThumbnailVideo, string>> = {
  audio: 'Official audio',
  lyricVideo: 'Lyric video',
  visualizer: 'Visualizer',
  musicVideo: 'Official video',
};

/** The label of a thumbnail; absent or unknown: "Official audio" (an audio upload with no kit video). */
export function thumbnailLabel(video: string | null | undefined, locale?: KitTextLocale | null): string {
  const words = kitWords(locale);
  const labels: Record<ThumbnailVideo, string> = {
    audio: words.officialAudio,
    lyricVideo: words.lyricVideo,
    visualizer: words.visualizer,
    musicVideo: words.officialVideo,
  };
  return isThumbnailVideo(video) ? labels[video] : labels.audio;
}

/**
 * The texts of the thumbnail of one video: the one builder of the render (music24-api
 * `buildKitCard`) and of the Studio preview. No status and no URL (owner answers 2 and 4 of page i). Pure.
 */
export function buildThumbnailCard(input: {
  video: ThumbnailVideo | null | undefined;
  title: string;
  artist: string;
  /** The language of the label; absent: English. */
  locale?: KitTextLocale | null;
}): KitThumbnailCard {
  return {
    kind: 'thumbnail',
    title: input.title,
    artist: input.artist,
    label: thumbnailLabel(input.video, input.locale),
  };
}

/** The state of a release on a link preview: before the release day, from it, or with no date. */
export type LinkPreviewState = 'before' | 'out' | 'noDate';

/** The state of the link preview badge (page m): the release date decides it. Pure. */
export function linkPreviewState(releaseDate: string | null, today: string): LinkPreviewState {
  if (releaseDate === null || releaseDateLabel(releaseDate) === null) {
    return 'noDate';
  }
  return isReleasedOn(releaseDate, today) ? 'out' : 'before';
}

/** "Friday" for `2026-10-16` ("Piątek" in Polish, first letter up); null for a date that the code cannot read. Pure. */
export function releaseWeekday(releaseDate: string | null, locale?: KitTextLocale | null): string | null {
  const parts = kitDateParts(releaseDate);
  if (parts === null) {
    return null;
  }
  const name = kitWords(locale).weekdaysLong[parts.weekday];
  return name === undefined ? null : capitalizeKitWord(name);
}

/**
 * The status badge of a link preview (page m): "Out" / "16 Oct" / "Friday" before the release day,
 * "Out" / "Now" from it, "Coming" / "Soon" with no date. Pure.
 */
export function linkPreviewBadge(
  releaseDate: string | null,
  today: string,
  locale?: KitTextLocale | null
): { top: string; value: string; sub: string | null } {
  const words = kitWords(locale);
  const state = linkPreviewState(releaseDate, today);
  if (state === 'out') {
    return { top: words.badgeOutTop, value: words.badgeOutValue, sub: null };
  }
  const label = releaseDateLabel(releaseDate, locale);
  if (state === 'noDate' || label === null) {
    return { top: words.badgeComingTop, value: words.badgeComingValue, sub: null };
  }
  return { top: words.badgeBeforeTop, value: label, sub: releaseWeekday(releaseDate, locale) };
}

/**
 * The call to action of a SmartLink of a song (owner answer 4 of page m): "Pre-save now" before the
 * release day when the song has a published pre-save page, "Listen now" from the release day on,
 * "Coming soon" with no date or no published pre-save page. Pure.
 */
export function smartLinkCallToAction(input: {
  releaseDate: string | null;
  today: string;
  hasPresave: boolean;
}): string {
  const state = linkPreviewState(input.releaseDate, input.today);
  if (state === 'out') {
    return CARD_COPY.listenNow;
  }
  return state === 'before' && input.hasPresave ? CARD_COPY.preSaveNow : CARD_COPY.comingSoon;
}

/**
 * The og:description of a SmartLink of a song: the call to action first, then the release date
 * after "Pre-save now" ("Pre-save now. Out Fri 16 Oct."), then the text of the page widgets.
 * "Coming soon" carries no date (page m: the state with no date or no published pre-save page). Pure.
 */
export function smartLinkMetaDescription(input: {
  releaseDate: string | null;
  today: string;
  hasPresave: boolean;
  widgets: string;
}): string {
  const cta = smartLinkCallToAction(input);
  const day = cta === CARD_COPY.preSaveNow ? releaseDayLabel(input.releaseDate) : null;
  const lead = day === null ? `${cta}.` : `${cta}. ${CARD_COPY.out} ${day}.`;
  const rest = input.widgets.trim();
  return rest === '' ? lead : `${lead} ${rest}`;
}

/**
 * The texts of a link preview (page m, the "with title" layout): "New single", the title, the
 * artist and the status badge by the release date. The one builder of the render (music24-api
 * `buildKitCard`), the Studio preview and the SmartLink fallback image of sml-preview. Pure.
 */
export function buildLinkPreviewCard(input: {
  title: string;
  artist: string;
  releaseDate: string | null;
  today: string;
  /** The language of the words; absent: English. */
  locale?: KitTextLocale | null;
}): KitLinkPreviewCard {
  return {
    kind: 'linkPreview',
    kicker: kitWords(input.locale).newSingle,
    title: input.title,
    artist: input.artist,
    badge: linkPreviewBadge(input.releaseDate, input.today, input.locale),
  };
}
